import { Network } from '@capacitor/network';
import { isNativeApp } from '@/utils/platform';

/**
 * Whether there is a connection, and what kind.
 *
 * The kind matters as much as the fact: fetching a deep audio buffer
 * unprompted is defensible on Wi-Fi and rude on a metered cellular plan, so
 * anything that spends bandwidth on the user's behalf needs to know which it
 * is rather than just "online".
 *
 * Two sources, because neither covers both platforms:
 *
 * - Native uses `@capacitor/network`, which reports the real radio state. The
 *   web APIs are unreliable inside WKWebView under capacitor://localhost, and
 *   `navigator.connection` does not exist on iOS Safari at all.
 * - Web uses `navigator.onLine` plus the online/offline events, which is all a
 *   browser reliably offers. Connection type is reported as 'unknown' rather
 *   than guessed — a wrong guess is worse than an honest absence, because
 *   callers treat 'cellular' as a reason to hold back.
 */

export type ConnectionType = 'wifi' | 'cellular' | 'none' | 'unknown';

export interface NetworkState {
  /** False only when we are confident there is no connection. */
  isOnline: boolean;
  connectionType: ConnectionType;
}

/**
 * Optimistic default. Until the first reading arrives, assume a connection:
 * a false "offline" would block work that would have succeeded, whereas an
 * optimistic guess merely fails the way it does today.
 */
export const INITIAL_NETWORK_STATE: NetworkState = {
  isOnline: true,
  connectionType: 'unknown',
};

const normalizeConnectionType = (raw: string | undefined): ConnectionType => {
  switch (raw) {
    case 'wifi':
    case 'cellular':
    case 'none':
      return raw;
    default:
      // The plugin also reports 'unknown', and a future version could report
      // something new. Both mean the same thing here: do not assume.
      return 'unknown';
  }
};

const readWebState = (): NetworkState => {
  // navigator.onLine only ever proves the negative reliably: false means no
  // interface is up, true means an interface exists and says nothing about
  // whether traffic actually reaches anywhere.
  const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  return { isOnline, connectionType: isOnline ? 'unknown' : 'none' };
};

export const getNetworkState = async (): Promise<NetworkState> => {
  if (!isNativeApp()) {
    return readWebState();
  }
  try {
    const status = await Network.getStatus();
    return {
      isOnline: status.connected,
      connectionType: status.connected
        ? normalizeConnectionType(status.connectionType)
        : 'none',
    };
  } catch (error) {
    // Never let a plugin failure present as "offline" — that would disable
    // working features on a healthy connection.
    console.warn('[network] Could not read connection status.', error);
    return INITIAL_NETWORK_STATE;
  }
};

/**
 * Subscribe to changes. Returns an unsubscribe function.
 *
 * The listener is called on every reported change, including ones that only
 * alter the connection type (Wi-Fi to cellular while still online), because
 * that transition is exactly when a buffering policy needs to back off.
 */
export const subscribeToNetworkState = (
  onChange: (state: NetworkState) => void,
): (() => void) => {
  if (!isNativeApp()) {
    const handler = () => onChange(readWebState());
    window.addEventListener('online', handler);
    window.addEventListener('offline', handler);
    return () => {
      window.removeEventListener('online', handler);
      window.removeEventListener('offline', handler);
    };
  }

  const listener = Network.addListener('networkStatusChange', (status) => {
    onChange({
      isOnline: status.connected,
      connectionType: status.connected
        ? normalizeConnectionType(status.connectionType)
        : 'none',
    });
  });

  let cancelled = false;
  void listener.catch((error: unknown) => {
    console.warn('[network] Could not subscribe to connection changes.', error);
  });

  return () => {
    if (cancelled) return;
    cancelled = true;
    void listener.then((handle) => handle.remove()).catch(() => {
      // Already gone, or never attached. Nothing to clean up.
    });
  };
};

/**
 * True when spending significant bandwidth on the user's behalf is
 * appropriate without asking.
 *
 * 'unknown' counts as unmetered: on the web there is no buffering to hold
 * back, and being wrong here costs a normal page-worth of traffic rather than
 * the hundreds of megabytes a deep audio buffer would.
 */
export const isUnmetered = (state: NetworkState): boolean =>
  state.isOnline && state.connectionType !== 'cellular';
